import { useMemo } from 'react'

import { createQueryKeyStore } from '@lukemorales/query-key-factory'
import { skipToken, useQueries, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { ResultOf } from 'gql.tada'
import * as _ from 'lodash-es'

import type { FissionFund, Valuation } from '@repo/cms/cms_types'
import fund_meta from '@repo/cms/data/fission/fund_meta'
import { graphql_call } from '@repo/common/queries/schemas/server_user_query'

import {
  calc_nav_returns,
  type CommonReturns,
} from '../../../helpers/nav_helpers'
import {
  disableKeyWhenMissing,
  sort_by_and_convert_dates,
} from '../../helpers/utilities'
import {
  type AllFundSymbol,
  type FundSymbol,
  getTokenNames,
  type SimulatedFundSymbol,
  type TokenName,
} from '../products/products_names'

import { query_fund_navs, query_list_funds } from './funds_graphql'

export const keys = createQueryKeyStore({
  funds: {
    all: null,
    details: ({ symbol }: { symbol: string }) => ({
      queryKey: [{ symbol }],
      contextQueries: {
        // blockchain
        price_manager_address: null,
        total_tokens: null,
        deposit_limit: null,
        prices: null,

        // graphql
        daily_nav: null,
      },
    }),
  },
})

export type FundFindBy = {
  id?: RubyID
  symbol?: AllFundSymbol
}

const FundOrder: AllFundSymbol[] = [] // ['XARK', 'XAPO', 'XTEC']

export type AllFundType = TokenName &
  FissionFund & {
    id: RubyID
    price_per_share: number
    target_range_percentage: {
      lower: number
      upper: number
    }
    cash_total_value: number
    assets_held_value: number
    assets: {
      name: string
      fission_ticker: string
      value_held: number
    }[]
    metrics: {
      latest_aum: Valuation
      inception: CommonReturns
      one_year: CommonReturns
    }
  }

export type SimulatedFundType = AllFundType & {
  symbol: SimulatedFundSymbol
}

export type ActiveFundType = AllFundType & {
  symbol: FundSymbol
  token_address: CryptoAddress
  token_decimals: number
}

function nav_or_null(
  nav: {
    price_per_share: number
    published_at: string
  } | null,
) {
  if (nav == null) return null
  return {
    price_per_share: nav.price_per_share,
    date: nav.published_at,
  }
}

// eslint-disable-next-line complexity
function convert_fund(
  fund: ResultOf<typeof query_list_funds>['funds'][number],
) {
  const assets: AllFundType['assets'] = fund.fund_assets.nodes
    .map((c) => ({
      name: c.asset.name,
      fission_ticker: c.asset.fission_ticker!.split('-').slice(0, 3).join('-'),
      value_held: c.latest_published_nav?.shares_value_held ?? 0,
    }))
    .filter((c) => c.value_held > 0)

  const assets_held_value = assets.reduce((sum, c) => sum + c.value_held, 0)
  const cash_total_value = fund.latest_published_nav?.cash_total_value ?? 0

  const inception_nav = nav_or_null(fund.earliest_published_nav)
  const one_year_nav = nav_or_null(fund.one_year_published_nav)
  const latest_nav = nav_or_null(fund.latest_published_nav)

  return {
    ...getTokenNames(fund.symbol as AllFundSymbol)!,
    ...fund_meta[fund.symbol as AllFundSymbol],
    id: fund.id,
    symbol: fund.symbol as SimulatedFundSymbol,
    price_per_share:
      fund.latest_published_nav?.price_per_share ??
      fund.starting_price_per_share,
    target_range_percentage: {
      lower: fund.target_range_lower_percentage,
      upper: fund.target_range_upper_percentage,
    },
    cash_total_value: fund.latest_published_nav?.cash_total_value ?? 0,
    assets_held_value,
    assets,
    metrics: {
      latest_aum: {
        valuation: assets_held_value + cash_total_value,
        date:
          fund.latest_published_nav?.published_at ??
          dayjs().format('YYYY-MM-DD'),
      },
      inception: calc_nav_returns({
        past_nav: inception_nav,
        latest_nav,
        date_key: 'date',
        value_key: 'price_per_share',
      }),
      one_year: calc_nav_returns({
        past_nav: one_year_nav,
        latest_nav,
        date_key: 'date',
        value_key: 'price_per_share',
      }),
    },
  } satisfies SimulatedFundType
}

export function useListFunds() {
  const results = useQuery<(ActiveFundType | SimulatedFundType)[]>({
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: Infinity,
    queryKey: keys.funds.all.queryKey,
    queryFn: async () => {
      const data = await graphql_call({ query: query_list_funds })

      const funds = data.map((f) => {
        if (f.tradable) {
          return {
            ...convert_fund(f),
            symbol: f.symbol as FundSymbol,
            token_address: f.token_address!,
            token_decimals: f.token_decimals,
          } satisfies ActiveFundType
        }
        return {
          ...convert_fund(f),
          symbol: f.symbol as SimulatedFundSymbol,
        } satisfies SimulatedFundType
      })

      return _.sortBy(funds, ({ symbol }) => {
        const index = FundOrder.indexOf(symbol)
        return index < 0 ? symbol : index.toString()
      })
    },
  })

  return results
}

export function useListActiveFunds() {
  const all_funds_query = useListFunds()

  const active_funds = useMemo(() => {
    if (all_funds_query.data == undefined) return undefined
    return all_funds_query.data.filter(
      (f): f is ActiveFundType => 'token_address' in f,
    )
  }, [all_funds_query.data])

  return {
    ...all_funds_query,
    data: active_funds,
  }
}

export function useFundInfo<T extends AllFundSymbol | undefined>({
  symbol,
  fund_id,
}: {
  symbol?: T
  fund_id?: RubyID
}): {
  data: T extends FundSymbol
    ? ActiveFundType | undefined
    : T extends SimulatedFundSymbol | undefined
      ? SimulatedFundType
      : undefined
  isSuccess: boolean
  isLoading: boolean
  isError: boolean
  error: null | Error
} {
  const { data, isLoading, isError, error } = useListFunds()

  if ((symbol == undefined && fund_id == undefined) || data == null) {
    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
      data: undefined as any,
      isSuccess: false,
      isLoading,
      isError,
      error,
    }
  }

  const fund = data.find(
    (f) => f.symbol === symbol?.toUpperCase() || f.id == fund_id,
  )

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
  return { data: fund as any, isSuccess: true, isLoading, isError, error }
}

export type DailyNav = {
  price_per_share: number
  date: number
}

async function fetch_fund_daily_navs(id: RubyID): Promise<{
  prelaunch_daily_nav: DailyNav[]
  daily_nav: DailyNav[]
}> {
  const result = await graphql_call({
    query: query_fund_navs,
    variables: { id },
  })
  if (result == null) {
    return {
      prelaunch_daily_nav: [],
      daily_nav: [],
    }
  }

  return {
    prelaunch_daily_nav: [],
    daily_nav: sort_by_and_convert_dates(result.published_navs.nodes),
  }
}

export function useFundDailyNavs({
  symbol,
}: {
  symbol: AllFundSymbol | undefined
}) {
  const { data } = useFundInfo({ symbol })
  const id = data?.id

  const enabled = id != null && symbol != null

  return useQuery({
    queryKey: keys.funds.details({ symbol: disableKeyWhenMissing(symbol) })._ctx
      .daily_nav.queryKey,
    queryFn: !enabled ? skipToken : async () => fetch_fund_daily_navs(id),
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: Infinity,
  })
}

export type FundsDailyNavs = Record<AllFundSymbol, DailyNav[] | undefined>

export function useFundsDailyNavs({
  funds = [],
}: {
  funds: { symbol: AllFundSymbol; id: RubyID }[] | undefined
}) {
  return useQueries({
    queries: funds.map(({ symbol, id }) => ({
      queryKey: keys.funds.details({ symbol })._ctx.daily_nav.queryKey,
      queryFn: () => fetch_fund_daily_navs(id),
      staleTime: 60 * 60 * 1000, // 1 hour
      gcTime: Infinity,
    })),
    combine: (results) => {
      const daily_navs = funds.reduce(
        // eslint-disable-next-line max-params
        (acc, { symbol }, index) => {
          acc[symbol] = results[index]?.data?.daily_nav
          return acc
        },
        {} as FundsDailyNavs,
      )

      const timestamps = _.flatMap(daily_navs, (navs) => _.map(navs, 'date'))
      const latest_nav_timestamp = _.max(timestamps) ?? 0

      return {
        daily_navs,
        latest_nav_timestamp,
        isSuccess: results.every((r) => r.isSuccess),
        daily_navs_loaded: _.every(daily_navs, (navs) => navs != null),
      }
    },
  })
}
